import React from "react";
import { Link } from "react-router-dom";
import { History, KeyRound, ShieldCheck, UserRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import { useMyAuditLogs } from "../../hooks/useAdmin";

/**
 * The signed-in staff member's own record.
 *
 * The permission list is the point of this page, not decoration: "why can't I
 * see Settings?" was previously unanswerable from inside the console, so it
 * became a message to whoever administers roles. Showing the grant directly
 * turns that into something the person can read for themselves.
 */
export function AdminProfile() {
  const { user } = useAuth();
  const { role, permissions, can } = usePermissions();
  const canReadFullTrail = can(PERMISSIONS.AUDIT_READ);

  // Your own entries, from a self-scoped endpoint that needs no `audit:read`.
  // Gating this on the oversight permission left `operations` - whose
  // force-complete actions are the most heavily audited thing in the console -
  // with no activity section at all.
  const { entries, isLoading } = useMyAuditLogs({ limit: 10 });

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
          Your account
        </h2>
        <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
          Staff Profile
        </h1>
      </div>

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#C0F6B7]/40 text-[#0A4730] grid place-items-center">
            <UserRound size={24} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-[#0A2540] text-lg">
              {user?.full_name || "Unnamed staff member"}
            </h3>
            <p className="text-sm text-slate-400 break-all">{user?.email || "—"}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mt-6">
          <Field label="Role" value={role || "—"} capitalize />
          <Field label="Permissions" value={`${permissions.length} granted`} />
          <Field label="Last sign-in" value={formatDate(user?.last_login_at)} />
          <Field label="Staff ID" value={user?.id || "—"} mono />
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={16} className="text-[#0A4730]" />
          <h3 className="text-sm font-semibold text-[#0A2540]">What your role allows</h3>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Granted by the <span className="capitalize font-semibold">{role || "—"}</span>{" "}
          role. Navigation hides sections you cannot use, but every request is
          checked by the API regardless.
        </p>

        {permissions.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">
            This account holds no staff permissions.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {permissions.map((permission) => (
              <span
                key={permission}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5F9F4] border border-[#C0F6B7]/60 text-xs font-medium text-[#0A4730] font-mono"
              >
                <ShieldCheck size={12} />
                {permission}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <History size={16} className="text-[#0A4730]" />
            <h3 className="text-sm font-semibold text-[#0A2540]">
              Your recent actions
            </h3>
          </div>
          {/* Only offered to a reader who can actually open the full log. */}
          {canReadFullTrail && (
            <Link
              to={`/admin/audit?actor_user_id=${user?.id ?? ""}`}
              className="text-xs font-semibold text-[#0A4730] hover:underline underline-offset-4"
            >
              View full trail
            </Link>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-400 py-2">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">
            You have not taken any audited actions yet.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {entries.map((entry) => (
              <li key={entry.id} className="py-3 first:pt-0 last:pb-0">
                <p className="text-sm text-[#0A2540]">
                  {entry.action.replaceAll(".", " › ").replaceAll("_", " ")}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {entry.target_type}
                  {entry.target_id ? ` · ${entry.target_id}` : ""}
                  <span className="mx-1.5">·</span>
                  {formatDate(entry.created_at)}
                </p>
                {entry.reason && (
                  <p className="text-xs text-[#0A2540]/70 mt-1 italic break-words">
                    “{entry.reason}”
                  </p>
                )}
              </li>
            ))}
        </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, capitalize = false, mono = false }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      <p
        className={`text-[#0A2540] break-all ${capitalize ? "capitalize" : ""} ${
          mono ? "font-mono text-xs" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Never";
}
